import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Typography,
	Grid,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Checkbox,
	FormControlLabel,
	Paper,
	alpha,
	Collapse,
	CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ScheduleIcon from '@mui/icons-material/Schedule'
import WorkIcon from '@mui/icons-material/Work'
import DescriptionIcon from '@mui/icons-material/Description'
import React, { useEffect, useState } from 'react'
import WorkItemList from './workItemAssociation/WorkItemList'
import {
	createChangeOrder,
	getRevisionsOfChangeOrder,
	reviseChangeOrder,
} from '../../services/changeOrderService'
import { useChangeOrderContext } from './ChangeOrderContext'
import { ManualChangeOrder } from '../../types/Work/ChangeOrder'
import { showErrorToast } from '../../services/CommonServices/alertServices'

type ChangeOrderFormData = {
	hasCostImpact: boolean
	hasScheduleImpact: boolean
	scheduleImpact: string
	scheduleImpactUnit: 'days' | 'hours'
	laborCost: string
	materialCost: string
	equipmentCost: string
	subcontractorCost: string
}

function validateChangeOrderForm(data: ChangeOrderFormData): boolean {
	if (data.hasCostImpact) {
		const labor = Number(data.laborCost)
		const material = Number(data.materialCost)
		const equipment = Number(data.equipmentCost)
		const subcontractor = Number(data.subcontractorCost)
		const hasAnyCost = labor > 0 || material > 0 || equipment > 0 || subcontractor > 0
		if (!hasAnyCost) {
			showErrorToast({ message: 'Enter at least one cost amount when cost impact is selected.' })
			return false
		}
	}

	if (data.hasScheduleImpact) {
		const impact = data.scheduleImpact.trim()
		const impactNum = Number(impact)
		if (impact === '' || Number.isNaN(impactNum) || impactNum <= 0) {
			showErrorToast({ message: 'Enter a valid schedule impact value (greater than zero).' })
			return false
		}
		if (!data.scheduleImpactUnit) {
			showErrorToast({ message: 'Select a schedule impact unit (days or hours).' })
			return false
		}
	}

	return true
}

export default function CreateChangeOrderButton() {
	const [open, setOpen] = useState(false)
	const { detailsToRevise, setDetailsToRevise, loadChangeOrders, setRevisions } =
		useChangeOrderContext()
	const [formData, setFormData] = useState({
		hasCostImpact: false,
		hasScheduleImpact: false,
		scheduleImpact: '',
		scheduleImpactUnit: 'days' as 'days' | 'hours',
		laborCost: '',
		materialCost: '',
		equipmentCost: '',
		subcontractorCost: '',
		affectsWorkItem: false,
		description: '',
	})
	const [selectedWorkItems, setSelectedWorkItems] = useState<{ id: number; path: string }[]>([])
	const [creating, setCreating] = useState(false)
	useEffect(() => {
		if (detailsToRevise) {
			handleOpen()
			setFormData({
				hasCostImpact:
					detailsToRevise.laborCost > 0 ||
					detailsToRevise.materialCost > 0 ||
					detailsToRevise.equipmentCost > 0 ||
					detailsToRevise.subContractorCost > 0,
				hasScheduleImpact: detailsToRevise.scheduleImpact > 0,
				scheduleImpact: detailsToRevise.scheduleImpact.toString(),
				scheduleImpactUnit: detailsToRevise.scheduleImpactUnit,
				laborCost: detailsToRevise.laborCost.toString(),
				materialCost: detailsToRevise.materialCost.toString(),
				equipmentCost: detailsToRevise.equipmentCost.toString(),
				subcontractorCost: detailsToRevise.subContractorCost.toString(),
				affectsWorkItem: detailsToRevise.workItems.length > 0,
				description: detailsToRevise.description,
			})
			setSelectedWorkItems(
				detailsToRevise.workItems.map((workItem) => ({ id: workItem.id, path: workItem.path })),
			)
		}
	}, [detailsToRevise])
	const handleOpen = () => {
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
		// Reset form when closing
		setFormData({
			hasCostImpact: false,
			hasScheduleImpact: false,
			scheduleImpact: '',
			scheduleImpactUnit: 'days',
			laborCost: '',
			materialCost: '',
			equipmentCost: '',
			subcontractorCost: '',
			affectsWorkItem: false,
			description: '',
		})
		setSelectedWorkItems([])
		setDetailsToRevise(undefined)
	}

	const handleCreateChangeOrder = async () => {
		if (!validateChangeOrderForm(formData)) return

		setCreating(true)
		try {
			const request: ManualChangeOrder = {
				hasCostImpact: formData.hasCostImpact,
				hasScheduleImpact: formData.hasScheduleImpact,
				scheduleImpact: Number(formData.scheduleImpact),
				scheduleImpactUnit: formData.scheduleImpactUnit,
				laborCost: Number(formData.laborCost),
				materialCost: Number(formData.materialCost),
				equipmentCost: Number(formData.equipmentCost),
				subContractorCost: Number(formData.subcontractorCost),
				workItemIds: selectedWorkItems.map((workItem) => workItem.id),
				description: formData.description,
			}
			if (detailsToRevise) {
				await reviseChangeOrder(detailsToRevise.changeOrderId, request)
				const revisions = await getRevisionsOfChangeOrder(detailsToRevise.changeOrderId)
				setRevisions((prev) => {
					const newMap = new Map(prev)
					newMap.set(detailsToRevise.changeOrderId, revisions)
					return newMap
				})
			} else {
				await createChangeOrder(request)
			}
			await loadChangeOrders()
		} finally {
			setCreating(false)
			handleClose()
		}
	}

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}))
	}

	return (
		<>
			<Button
				variant='outlined'
				onClick={handleOpen}
				startIcon={<AddIcon />}
				sx={{
					textTransform: 'none',
					minWidth: '250px',
					paddingX: 4,
					paddingY: 1.5,
					color: 'primary.main',
				}}
			>
				Create New Change Order
			</Button>

			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth={formData.affectsWorkItem ? 'xl' : 'md'}
				fullWidth
				PaperProps={{
					sx: (theme) => ({
						borderRadius: 3,
						boxShadow: theme.shadows[8],
						height: '90vh',
						display: 'flex',
						flexDirection: 'column',
					}),
				}}
			>
				<DialogTitle
					sx={(theme) => ({
						pb: 2,
						pt: 3,
						background: `linear-gradient(to right, ${alpha(
							theme.palette.primary.main,
							0.08,
						)}, ${alpha(theme.palette.primary.main, 0.02)})`,
						borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
					})}
				>
					<Typography variant='h5' sx={{ fontWeight: 700, color: 'primary.main' }}>
						{detailsToRevise ? 'Revise Change Order' : 'Create New Change Order'}
					</Typography>
					<Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
						{detailsToRevise
							? 'Revise the change order details below'
							: 'Fill out the form below to create a new change order'}
					</Typography>
				</DialogTitle>
				<IconButton
					onClick={handleClose}
					sx={(theme) => ({
						position: 'absolute',
						right: 12,
						top: 12,
						color: theme.palette.grey[600],
						'&:hover': {
							backgroundColor: alpha(theme.palette.error.main, 0.08),
							color: theme.palette.error.main,
						},
					})}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent sx={{ p: 3 }}>
					<Box
						sx={{
							display: 'flex',
							gap: 3,
							height: '100%',
							minHeight: 0,
							flexDirection: formData.affectsWorkItem ? 'row' : 'column',
						}}
					>
						{/* Form Section */}
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 3,
								flex: formData.affectsWorkItem ? '0 0 35%' : '1',
								minWidth: 0,
								minHeight: 0,
								height: '100%',
							}}
						>
							{/* Cost Impact Section */}
							<Paper
								variant='outlined'
								sx={(theme) => ({
									p: 2,
									borderRadius: 2,
									backgroundColor: alpha(theme.palette.primary.main, 0.02),
									border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
									transition: 'all 0.2s ease-in-out',
									'&:hover': {
										borderColor: alpha(theme.palette.primary.main, 0.2),
									},
								})}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
									<AttachMoneyIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
									<FormControlLabel
										control={
											<Checkbox
												checked={formData.hasCostImpact}
												onChange={(e) => {
													const checked = e.target.checked
													setFormData((prev) => ({
														...prev,
														hasCostImpact: checked,
														laborCost: checked ? prev.laborCost : '',
														materialCost: checked ? prev.materialCost : '',
														equipmentCost: checked ? prev.equipmentCost : '',
														subcontractorCost: checked ? prev.subcontractorCost : '',
													}))
												}}
												sx={{
													'& .MuiSvgIcon-root': {
														fontSize: 24,
													},
												}}
											/>
										}
										label={
											<Typography
												variant='subtitle1'
												sx={{ fontWeight: 600, color: 'text.primary' }}
											>
												Cost Impact
											</Typography>
										}
									/>
								</Box>
								<Collapse in={formData.hasCostImpact} timeout={300}>
									<Box sx={{ mt: 2, pl: 5 }}>
										<Grid container spacing={2.5}>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													label='Labor Cost'
													variant='outlined'
													type='number'
													value={formData.laborCost}
													onChange={(e) => handleInputChange('laborCost', e.target.value)}
													InputProps={{
														startAdornment: <InputAdornment position='start'>$</InputAdornment>,
													}}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													label='Material Cost'
													variant='outlined'
													type='number'
													value={formData.materialCost}
													onChange={(e) => handleInputChange('materialCost', e.target.value)}
													InputProps={{
														startAdornment: <InputAdornment position='start'>$</InputAdornment>,
													}}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													label='Equipment Cost'
													variant='outlined'
													type='number'
													value={formData.equipmentCost}
													onChange={(e) => handleInputChange('equipmentCost', e.target.value)}
													InputProps={{
														startAdornment: <InputAdornment position='start'>$</InputAdornment>,
													}}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													label='Sub-contractor Cost'
													variant='outlined'
													type='number'
													value={formData.subcontractorCost}
													onChange={(e) => handleInputChange('subcontractorCost', e.target.value)}
													InputProps={{
														startAdornment: <InputAdornment position='start'>$</InputAdornment>,
													}}
												/>
											</Grid>
										</Grid>
									</Box>
								</Collapse>
							</Paper>

							{/* Schedule Impact Section */}
							<Paper
								variant='outlined'
								sx={(theme) => ({
									p: 2,
									borderRadius: 2,
									backgroundColor: alpha(theme.palette.primary.main, 0.02),
									border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
									transition: 'all 0.2s ease-in-out',
									'&:hover': {
										borderColor: alpha(theme.palette.primary.main, 0.2),
									},
								})}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
									<ScheduleIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
									<FormControlLabel
										control={
											<Checkbox
												checked={formData.hasScheduleImpact}
												onChange={(e) => {
													const checked = e.target.checked
													setFormData((prev) => ({
														...prev,
														hasScheduleImpact: checked,
														scheduleImpact: checked ? prev.scheduleImpact : '',
													}))
												}}
												sx={{
													'& .MuiSvgIcon-root': {
														fontSize: 24,
													},
												}}
											/>
										}
										label={
											<Typography
												variant='subtitle1'
												sx={{ fontWeight: 600, color: 'text.primary' }}
											>
												Schedule Impact
											</Typography>
										}
									/>
								</Box>
								<Collapse in={formData.hasScheduleImpact} timeout={300}>
									<Box sx={{ mt: 2, pl: 5 }}>
										<Box sx={{ display: 'flex', gap: 1.5 }}>
											<TextField
												fullWidth
												label='Schedule Impact'
												variant='outlined'
												type='number'
												value={formData.scheduleImpact}
												onChange={(e) => handleInputChange('scheduleImpact', e.target.value)}
												sx={{
													'& .MuiOutlinedInput-root': {
														backgroundColor: 'background.paper',
													},
												}}
											/>
											<FormControl sx={{ minWidth: 130 }}>
												<InputLabel>Unit</InputLabel>
												<Select
													value={formData.scheduleImpactUnit}
													label='Unit'
													onChange={(e) =>
														setFormData((prev) => ({
															...prev,
															scheduleImpactUnit: e.target.value as 'days' | 'hours',
														}))
													}
												>
													<MenuItem value='days'>Days</MenuItem>
													<MenuItem value='hours'>Hours</MenuItem>
												</Select>
											</FormControl>
										</Box>
									</Box>
								</Collapse>
							</Paper>

							{/* Work Item Association Section */}
							<Paper
								variant='outlined'
								sx={(theme) => ({
									p: 2,
									borderRadius: 2,
									backgroundColor: alpha(theme.palette.primary.main, 0.02),
									border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
								})}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
									<WorkIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
									<FormControlLabel
										control={
											<Checkbox
												checked={formData.affectsWorkItem}
												onChange={(e) => {
													const checked = e.target.checked
													setFormData((prev) => ({
														...prev,
														affectsWorkItem: checked,
													}))
													if (!checked) {
														setSelectedWorkItems([])
													}
												}}
												sx={{
													'& .MuiSvgIcon-root': {
														fontSize: 24,
													},
												}}
											/>
										}
										label={
											<Typography
												variant='subtitle1'
												sx={{ fontWeight: 600, color: 'text.primary' }}
											>
												Work Item Association
											</Typography>
										}
									/>
								</Box>
								{formData.affectsWorkItem && selectedWorkItems.filter(Boolean).length > 0 && (
									<Box sx={{ mt: 2, pl: 5 }}>
										<Typography
											variant='body2'
											sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
										>
											Selected work items:
										</Typography>
										<Box
											component='ul'
											sx={{
												m: 0,
												pl: 2.5,
												typography: 'body2',
												color: 'text.primary',
												'& li': {
													mb: 0.5,
													overflowWrap: 'break-word',
													wordBreak: 'break-word',
												},
											}}
										>
											{selectedWorkItems.filter(Boolean).map((workItem, i) => {
												const segments = workItem.path.split(' > ')
												return (
													<li key={i}>
														{segments.map((segment, j) => (
															<React.Fragment key={j}>
																{j > 0 && (
																	<>
																		<wbr />
																		{' > '}
																	</>
																)}
																{segment}
															</React.Fragment>
														))}
													</li>
												)
											})}
										</Box>
									</Box>
								)}
							</Paper>

							{/* Description Section */}
							<Paper
								variant='outlined'
								sx={(theme) => ({
									p: 2.5,
									borderRadius: 2,
									backgroundColor: alpha(theme.palette.primary.main, 0.02),
									border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
									transition: 'all 0.2s ease-in-out',
									'&:hover': {
										borderColor: alpha(theme.palette.divider, 0.2),
									},
									height: '100%',
								})}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
									<DescriptionIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
									<Typography variant='subtitle1' sx={{ fontWeight: 600, color: 'text.primary' }}>
										Description
									</Typography>
								</Box>
								<TextField
									fullWidth
									label='Provide details about this change order'
									variant='outlined'
									multiline
									rows={5}
									value={formData.description}
									onChange={(e) => handleInputChange('description', e.target.value)}
									placeholder='Enter a detailed description of the change order, including scope,
									 rationale, and any relevant context...'
								/>
							</Paper>
						</Box>

						{/* Work Item List Panel */}
						{formData.affectsWorkItem && (
							<Box
								sx={{
									flex: '0 0 63%',
									minWidth: 0,
									display: 'flex',
									flexDirection: 'column',
									height: '100%',
								}}
							>
								<Paper
									variant='outlined'
									sx={(theme) => ({
										borderRadius: 2,
										backgroundColor: alpha(theme.palette.primary.main, 0.02),
										border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
										display: 'flex',
										flexDirection: 'column',
										overflow: 'hidden',
										flex: 1,
										height: '100%',
									})}
								>
									<WorkItemList
										key='work-item-list'
										selectedWorkItems={selectedWorkItems}
										onSelectionChange={setSelectedWorkItems}
									/>
								</Paper>
							</Box>
						)}
					</Box>
				</DialogContent>
				<DialogActions
					sx={(theme) => ({
						justifyContent: 'space-between',
						px: 3,
						py: 2.5,
						borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
						backgroundColor: alpha(theme.palette.grey[50], 0.5),
					})}
				>
					<Button
						onClick={handleClose}
						variant='outlined'
						disabled={creating}
						sx={{
							color: 'primary.main',
							minWidth: 120,
							textTransform: 'none',
							fontWeight: 600,
							borderRadius: 2,
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleCreateChangeOrder}
						disabled={creating}
						variant='contained'
						color='primary'
						sx={{
							minWidth: 120,
							textTransform: 'none',
							fontWeight: 600,
							borderRadius: 2,
							boxShadow: 2,
							'&:hover': {
								boxShadow: 4,
							},
						}}
					>
						{creating ? (
							<CircularProgress size={24} sx={{ m: 0, p: 0 }} />
						) : detailsToRevise ? (
							'Revise'
						) : (
							'Create'
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
